// Klient API profili i postępu (urządzenie z kluczem): lista profili, zmiana wyglądu, synchronizacja postępu. Serwer: server/profiles.ts.
// Każdy wynik to nazwany przypadek (bez wyjątków), z którego aplikacja decyduje, co pokazać: ekran logowania, ekran wyboru profilu albo tryb lokalny.
import type { Progress } from '../types.ts';
import { cleanGender } from './gender.ts';
import type { Gender } from './gender.ts';
import { lookOf } from './look.ts';
import type { Look } from './look.ts';
import { validateProgress } from './progressMerge.ts';
import { isIsoDate } from './ranking.ts';
import type { RankPlayer } from './ranking.ts';

export interface ProfileInfo extends Look {
  id: string;
  name: string;
  /** Płeć dziecka (formy gramatyczne w tekstach); '' = nie ustawiono. Ustawia ją rodzic. */
  gender: Gender;
  /** Czy rodzic ustawił temu profilowi PIN (4 cyfry): wtedy wybór profilu pyta o PIN. Skrótu ani samego PIN-u urządzenie nie dostaje. */
  hasPin: boolean;
  /** Czy rodzic włączył temu profilowi zapis nagrań do strojenia oceny (SPEC §23.39). Brak pola = wyłączone. */
  recordVoice?: boolean;
}

export type ListResult =
  | { kind: 'ok'; profiles: ProfileInfo[]; /** Ile aktywnych profili wolno mieć (serwer: PROFILE_LIMIT); brak w odpowiedzi = domyślne. */ limit: number }
  /** Serwer nie zna tego urządzenia (brak klucza, zły klucz albo urządzenie wyłączone w panelu rodzica). */
  | { kind: 'unpaired'; revoked: boolean }
  /** Ten adres nie ma backendu (hosting statyczny, serwer deweloperski). */
  | { kind: 'no-server' }
  | { kind: 'offline' }
  /** Backend jest, ale nie działa (np. brak migracji bazy): aplikacja przechodzi na tryb lokalny, a rodzic widzi powód. */
  | { kind: 'error'; detail: string };

/** Wynik sprawdzenia PIN-u profilu na serwerze. */
export type VerifyResult =
  | { kind: 'ok' }
  /** Zły PIN; `left` = ile prób zostało do blokady (null, gdy serwer nie podał). */
  | { kind: 'bad'; left: number | null }
  /** Za dużo błędów: profil jest zablokowany na kilka minut. */
  | { kind: 'locked'; retryAfterMin: number }
  | { kind: 'unpaired' }
  /** Profil został usunięty albo zarchiwizowany. */
  | { kind: 'gone' }
  | { kind: 'offline' }
  | { kind: 'error'; detail: string };

export type PutResult =
  | { kind: 'ok'; rev: number; epoch: number; progress: Progress }
  /** Rodzic wyzerował postęp: ta kopia na urządzeniu jest nieaktualna i ma zostać zastąpiona odpowiedzią. */
  | { kind: 'reset'; rev: number; epoch: number; progress: Progress }
  /** Profil został zarchiwizowany albo usunięty. */
  | { kind: 'gone' }
  | { kind: 'unpaired' }
  | { kind: 'offline' }
  | { kind: 'error'; detail: string };

/** Ranking graczy (patch-35): rodzeństwo z punktami; wynik jak w pozostałych wywołaniach — przypadki zamiast wyjątków. */
export type RankingResult =
  | { kind: 'ok'; players: RankPlayer[] }
  | { kind: 'unpaired' }
  /** Ten adres nie ma backendu (tryb lokalny) — rankingu nie ma z kim liczyć. */
  | { kind: 'no-server' }
  | { kind: 'offline' }
  | { kind: 'error'; detail: string };

export interface ProfilesClient {
  list(): Promise<ListResult>;
  /** Pobiera punkty wszystkich profili rodziny; `today` = dzisiejsza data dziecka (RRRR-MM-DD), od niej liczy się „ten tydzień”. */
  ranking(today: string): Promise<RankingResult>;
  /** Zmienia wygląd i/lub imię własnego profilu; zwraca zapisany profil albo null, gdy się nie udało. */
  setLook(id: string, look: Partial<Look> & { name?: string }): Promise<ProfileInfo | null>;
  /** Sprawdza PIN profilu (serwer liczy błędne próby i blokuje profil po pięciu w 15 minut). Profil bez PIN-u zawsze przechodzi. */
  verifyPin(id: string, pin: string): Promise<VerifyResult>;
  /** Wysyła kopię postępu; serwer ją scala ze swoją i odsyła wynik. `epoch` = null, gdy urządzenie jeszcze nie synchronizowało tego profilu. */
  putProgress(id: string, progress: Progress, epoch: number | null): Promise<PutResult>;
}

const ID = /^[A-Za-z0-9_-]{1,40}$/;
/** Tyle samo, co PROFILE_LIMIT na serwerze — używane tylko, gdy serwer nie podał limitu. */
export const DEFAULT_PROFILE_LIMIT = 8;

export function parseProfile(raw: unknown): ProfileInfo | null {
  const r = raw as { id?: unknown; name?: unknown } | null;
  if (!r || typeof r !== 'object' || typeof r.id !== 'string' || !ID.test(r.id) || typeof r.name !== 'string' || r.name.trim() === '') return null;
  return { id: r.id, name: r.name.trim().slice(0, 40), gender: cleanGender((r as { gender?: unknown }).gender) ?? '', hasPin: (r as { hasPin?: unknown }).hasPin === true, ...((r as { recordVoice?: unknown }).recordVoice === true ? { recordVoice: true } : {}), ...lookOf(r) };
}

const points = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : 0);

/** Gracz z odpowiedzi serwera (null = zapis uszkodzony). */
export function parseRankPlayer(raw: unknown): RankPlayer | null {
  const p = parseProfile(raw);
  if (!p) return null;
  const r = raw as { week?: unknown; total?: unknown };
  return { id: p.id, name: p.name, gender: p.gender, avatar: p.avatar, theme: p.theme, week: points(r.week), total: points(r.total) };
}

async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body = (await res.json()) as unknown;
    return typeof body === 'object' && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
  } catch {
    return null; // np. strona HTML z hostingu bez backendu
  }
}

export function createProfilesClient(headers: () => Record<string, string>, fetchImpl: typeof fetch = (...a) => fetch(...a), base = './api'): ProfilesClient {
  const send = async (path: string, init: { method: string; body?: unknown }): Promise<Response | null> => {
    try {
      return await fetchImpl(`${base}/${path}`, {
        method: init.method,
        cache: 'no-store',
        headers: { ...headers(), ...(init.body === undefined ? {} : { 'content-type': 'application/json' }) },
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch {
      return null;
    }
  };
  const detailOf = (res: Response, body: Record<string, unknown> | null): string => (typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`);

  return {
    async list() {
      const res = await send('profiles', { method: 'GET' });
      if (!res) return { kind: 'offline' };
      const body = await readJson(res);
      if (res.status === 401) return { kind: 'unpaired', revoked: body?.error === 'device-revoked' };
      if (body === null) return res.status >= 500 ? { kind: 'error', detail: `HTTP ${res.status}` } : { kind: 'no-server' };
      if (!res.ok) return { kind: 'error', detail: detailOf(res, body) };
      if (!Array.isArray(body.profiles)) return { kind: 'no-server' };
      const limit = typeof body.limit === 'number' && Number.isInteger(body.limit) && body.limit > 0 ? body.limit : DEFAULT_PROFILE_LIMIT;
      return { kind: 'ok', profiles: body.profiles.map(parseProfile).filter((p): p is ProfileInfo => p !== null), limit };
    },

    async ranking(today) {
      const res = await send(`ranking?today=${encodeURIComponent(isIsoDate(today) ? today : '')}`, { method: 'GET' });
      if (!res) return { kind: 'offline' };
      const body = await readJson(res);
      if (res.status === 401) return { kind: 'unpaired' };
      if (body === null) return res.status >= 500 ? { kind: 'error', detail: `HTTP ${res.status}` } : { kind: 'no-server' };
      if (!res.ok) return { kind: 'error', detail: detailOf(res, body) };
      if (!Array.isArray(body.players)) return { kind: 'no-server' };
      return { kind: 'ok', players: body.players.map(parseRankPlayer).filter((p): p is RankPlayer => p !== null) };
    },

    async setLook(id, look) {
      const res = await send('profiles', { method: 'POST', body: { id, ...(look.name !== undefined ? { name: look.name } : {}), ...(look.avatar !== undefined ? { avatar: look.avatar } : {}), ...(look.theme !== undefined ? { theme: look.theme } : {}) } });
      if (!res || !res.ok) return null;
      return parseProfile((await readJson(res))?.profile);
    },

    async verifyPin(id, pin) {
      const res = await send('profiles', { method: 'POST', body: { action: 'verify', id, pin } });
      if (!res) return { kind: 'offline' };
      const body = await readJson(res);
      if (res.ok && body?.ok === true) return { kind: 'ok' };
      if (res.status === 401 && body?.error === 'bad-pin') return { kind: 'bad', left: typeof body.left === 'number' ? body.left : null };
      if (res.status === 401) return { kind: 'unpaired' };
      if (res.status === 429) return { kind: 'locked', retryAfterMin: Math.max(1, Math.round((Number(body?.retryAfterSec) || 900) / 60)) };
      if (res.status === 404) return { kind: 'gone' };
      return { kind: 'error', detail: body === null ? `HTTP ${res.status}` : detailOf(res, body) };
    },

    async putProgress(id, progress, epoch) {
      const res = await send('progress', { method: 'PUT', body: { profile: id, progress, epoch } });
      if (!res) return { kind: 'offline' };
      const body = await readJson(res);
      if (res.status === 401) return { kind: 'unpaired' };
      if (res.status === 404 && body?.error === 'profile-not-found') return { kind: 'gone' };
      if (res.status === 409 && body?.error === 'reset') {
        const p = validateProgress(body.progress);
        return p && typeof body.epoch === 'number' && typeof body.rev === 'number' ? { kind: 'reset', rev: body.rev, epoch: body.epoch, progress: p } : { kind: 'error', detail: 'zła odpowiedź serwera' };
      }
      if (!res.ok || body === null) return { kind: 'error', detail: body === null ? `HTTP ${res.status}` : detailOf(res, body) };
      const p = validateProgress(body.progress);
      if (!p || typeof body.rev !== 'number' || typeof body.epoch !== 'number') return { kind: 'error', detail: 'zła odpowiedź serwera' };
      return { kind: 'ok', rev: body.rev, epoch: body.epoch, progress: p };
    },
  };
}
